import _ from 'underscore';

/**
 * Remove a single key from the input object.
 *
 * Typesafe alternative to _.omit for a single key.
 *
 * Prefer using omit below for new usages.
 */
export default function omitKey<T, K extends keyof T>(obj: T, key: K): Omit<T, K> {
  const {[key]: k, ...rest} = obj;
  return rest;
}

/**
 * Remove specified keys from the input object.
 *
 * Typesafe alternative to _.omit for many keys. Prefer over omitKey for new usages.
 *
 * Example:
 *
 * interface User {
 *   name: string;
 *   age: number;
 *   email: string;
 * }
 *
 * const user: User = {...};
 *
 * const userWithoutSensitiveData = omit(user, 'age', 'email');
 *
 * userWithoutSensitiveData.name; // typechecks
 * userWithoutSensitiveData.age; // does not typecheck
 * userWithoutSensitiveData.email; // does not typecheck
 */
export function omit<T extends object, K extends [...Array<keyof T>]>(
  obj: T,
  ...keys: K
): {
  [K2 in Exclude<keyof T, K[number]>]: T[K2];
} {
  // eslint-disable-next-line no-restricted-syntax
  return _.omit(obj, ...(keys as Array<string>));
}
